import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Text,
  VStack,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  TabIndicator,
  TabPanels,
  TabPanel,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { useFetch } from 'use-http';
import { useParams } from 'react-router';
import { api, makeHeaders } from '../../util/api';
import Loading from '../Misc/Loading';
import { ERROR_MESSAGE, useNotify } from '../../hooks/useNotify';
import { Survey, SurveyResponse } from './types';
import { groupByProperty } from '../../util/array';
import EmptyContent from '../EmptyContent';
import SurveyResponseCard from './SurveyResponseCard';

interface SurveyResponsesProps {
  survey: Survey | null;
}

const SurveyResponses: FC<SurveyResponsesProps> = ({
  survey,
}) => {
  const notify = useNotify();
  const { id } = useParams();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  // TODO: filter by archived in the backend
  const filteredResponses = responses.filter((response) => !response.archived);
  const groups = useMemo(() => {
    return groupByProperty(filteredResponses, 'validated');
  }, [filteredResponses]);

  const validadedResponses = groups['true'] || [];
  const notValidadedResponses = groups['false'] || [];
  const tabPanels = [filteredResponses, validadedResponses, notValidadedResponses];

  const {
    error,
    cache,
    loading,
    response,
    get,
  } = useFetch(`${api}/surveys/${id}/responses`, makeHeaders());

  const refreshSurveyResponses = async () => {
    try {
      cache.clear();
      const responses = await get();
      if (!error && response.ok && responses) {
        setResponses(responses);
      } else {
        throw new Error();
      }
    } catch {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  useEffect(() => {
    refreshSurveyResponses();
  }, []); // eslint-disable-line

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const maxBadgeCount = 99;
  const formatBadgeCount = (count: number) => {
    if (count > maxBadgeCount) {
      return `${maxBadgeCount}+`;
    }
    return count;
  };

  return (
    <VStack
      align="stretch"
      rounded="md"
      borderWidth='1px'
      borderColor={borderColor}
      _notLast={{ borderBottom: 'none' }}
      overflow='hidden'
      w="full"
      bgColor={useColorModeValue('white', 'gray.800')}
    >
      <Tabs align='end' isFitted >
        <TabList>
          <Tab>
            <Text>Todas</Text>
          </Tab>
          <Tab>
            <Text>Validadas</Text>
          </Tab>
          <Tab>
            <HStack direction="row" align="center" position='relative'>
              <Text>No validadas</Text>
              {groups['false']?.length > 0 && (
                <Badge
                  variant='solid'
                  colorScheme='brand'
                  borderRadius={8}
                  minWidth={5}
                >
                  <Text>{formatBadgeCount(groups['false']?.length)}</Text>
                </Badge>
              )}
            </HStack>
          </Tab>
        </TabList>
        {!loading && (
          <TabIndicator
            mt="-2px"
            height="2px"
            width="full"
          />
        )}
        <Loading isLoading={loading} />
        <TabPanels>
          {tabPanels.map((tabPanel, index) => (
            <TabPanel key={index} p={0}>
              {!tabPanel.length && <EmptyContent title='No hay respuestas' />}
              {tabPanel.map((response) => (
                <SurveyResponseCard
                  key={response.id}
                  survey={survey}
                  response={response}
                  onChange={refreshSurveyResponses}
                />))}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default SurveyResponses;
